import { contactWrap, FlowTemplate, pill, sectionIcon, singleColumnPage, type RenderContext, type Zone } from "../base";
import {
  box,
  col,
  expanded,
  hrule,
  hspace,
  keep,
  linkText,
  padding,
  rich,
  row,
  sized,
  text,
  vspace,
  wrapBox,
  type Node,
  type PageSpec,
} from "../ir";
import { hasCategories, skillGroups, type RenderEntry, type RenderSection } from "../model";
import { hex } from "../theme";

/**
 * Code — for engineers: JetBrains Mono accents, `#` headings, outlined tech
 * chips and categorised skill groups. Still a single, ATS-safe column
 * underneath. (code_template.dart)
 */
export class CodeTemplate extends FlowTemplate {
  readonly info = {
    id: "code",
    name: "Code",
    tagline: "Developer-friendly with monospace accents",
    category: "tech" as const,
    layout: "One column",
    defaultAccent: 0xff059669,
    atsFriendly: true,
    supportsPhoto: false,
  };

  readonly fonts = { body: "inter" as const, usesMono: true };

  zoneWidth(c: RenderContext): number {
    return c.format.width - 80;
  }

  heading(c: RenderContext, s: RenderSection): Node {
    const t = c.t;
    return row([
      sectionIcon(c, s, t.accentText, t.fs(11)) ?? text("#", t.style(t.mono.bold, 10.6, { color: t.accentText, lineSpacing: 0 })),
      hspace(6),
      text(s.title, t.style(t.mono.bold, 10.2, { color: t.ink, lineSpacing: 0 })),
      hspace(10),
      expanded(box({ height: 1, borderBottom: { width: 0.8, color: hex(t.line), dashed: true } })),
    ]);
  }

  bulletGlyph(c: RenderContext): Node {
    return text(">", c.t.style(c.t.mono.bold, 8.4, { color: c.t.accentText, lineSpacing: 0 }));
  }

  entryHeader(c: RenderContext, e: RenderEntry): Node {
    const t = c.t;
    const title = e.title || e.subtitle;
    const subtitle = e.title ? e.subtitle : "";
    return col([
      row(
        [
          expanded(
            rich([
              [title, t.style(t.body.bold, 10.1, { color: t.ink })],
              subtitle ? [`  @ ${subtitle}`, t.style(t.mono.regular, 8.8, { color: t.accentText })] : null,
            ])
          ),
          e.dates && hspace(10),
          e.dates && text(e.dates, t.style(t.mono.regular, 7.8, { color: t.muted, lineSpacing: 0 })),
        ],
        { align: "start" }
      ),
      e.meta && padding({ top: 1.5 }, linkText(e.meta, t.style(t.mono.regular, 7.8, { color: t.muted, lineSpacing: 0 }), e.url)),
    ]);
  }

  private chip(c: RenderContext, value: string): Node {
    return pill(value, c.t.style(c.t.mono.regular, 7.6, { color: c.t.ink, lineSpacing: 0 }), {
      border: c.t.line,
      radius: 3,
      hPad: 5,
      vPad: 2,
    });
  }

  techTags(c: RenderContext, tags: string[]): Node {
    return wrapBox(
      tags.map((tag) => this.chip(c, tag)),
      { spacing: 4, runSpacing: 3 }
    );
  }

  chips(c: RenderContext, items: string[]): Node {
    return wrapBox(
      items.map((item) => this.chip(c, item)),
      { spacing: 4, runSpacing: 4 }
    );
  }

  skillsContent(c: RenderContext, s: RenderSection, z: Zone): Node[] {
    if (c.showSkillLevels) return this.levelList(c, s.skills, z, { grouped: hasCategories(s) });
    const t = c.t;
    if (!hasCategories(s)) return [this.chips(c, s.skills.map((x) => x.name))];
    return skillGroups(s).map(([key, list]) =>
      padding(
        { bottom: t.gap(5) },
        row(
          [
            sized(
              96,
              undefined,
              padding({ top: 2 }, text(key ? key.toLowerCase() : "other", t.style(t.mono.bold, 8, { color: t.accentText, lineSpacing: 0 })))
            ),
            expanded(this.chips(c, list.map((x) => x.name))),
          ],
          { align: "start" }
        )
      )
    );
  }

  build(c: RenderContext): PageSpec {
    const t = c.t;
    const m = c.model;
    const header = col([
      m.name && text(m.name, t.style(t.display.bold, 25, { color: t.ink, letterSpacing: -0.5, lineSpacing: 0 })),
      m.headline && vspace(5),
      m.headline && text(`// ${m.headline}`, t.style(t.mono.regular, 9.8, { color: t.accentText, lineSpacing: 0 })),
      m.contacts.length > 0 && vspace(10),
      m.contacts.length > 0 &&
        contactWrap(c, {
          style: t.style(t.mono.regular, 7.9, { color: t.text, lineSpacing: 0 }),
          iconColor: t.accent,
          spacing: 13,
        }),
      vspace(12),
      hrule(1.2, t.ink),
      vspace(t.gap(16)),
    ]);
    return singleColumnPage(c, [40, 38, 40, 32], [keep([header], true), ...this.allSections(c, m.sections)]);
  }
}
